import type { MrgReportFilterState } from '../../types/filters'
import type { MrgReportListParams, MrgReportMrgColumn } from '../../types/pages'
import {
  displayCell,
  formatDateCell,
  fullName,
  isWeekSummaryRow,
  moneyCell,
  padApplicantId,
} from '../../utils/mrgReportRowFormatters'

const MONEY_CELL =
  'whitespace-nowrap text-right tabular-nums font-medium text-slate-900'
const MONEY_HEADER = 'text-right'
const ID_CELL = 'whitespace-nowrap font-mono text-xs tabular-nums text-slate-700'
const DATE_CELL = 'whitespace-nowrap tabular-nums text-slate-700'
const PCT_CELL = 'whitespace-nowrap text-right tabular-nums text-slate-800'

export const DEFAULT_FILTER: MrgReportFilterState = {
  q: '',
  company_id: '',
  company_name: '',
  property_id: '',
  property_name: '',
  fromDt: '',
  toDt: '',
  claimfromDt: '',
  claimtoDt: '',
  rentalStatus: [],
}

export const DEFAULT_PARAMS: MrgReportListParams = {
  ...DEFAULT_FILTER,
  page: 1,
  perPage: 15,
  sortBy: 'desc',
  orderBy: 'contract_start_date',
}

export const EXPORT_REPORT_TYPE = 'reports_insurance_mrg'

export const MRG_COLUMNS: MrgReportMrgColumn[] = [
  {
    key: 'applicant_id',
    label: 'Applicant',
    sortable: true,
    className: ID_CELL,
    render: (row) =>
      isWeekSummaryRow(row)
        ? String(row.applicant_id ?? '')
        : padApplicantId(row.applicant_id),
  },
  {
    key: 'application_id',
    label: 'Application',
    sortable: true,
    className: ID_CELL,
    render: (row) => displayCell(row.application_id),
  },
  {
    key: 'first_name',
    label: 'Name',
    sortable: true,
    className: 'min-w-[8rem] whitespace-normal font-medium text-slate-900',
    render: (row) => fullName(row),
  },
  {
    key: 'company_name',
    label: 'Company',
    sortable: true,
    className: 'min-w-[9rem] whitespace-normal text-slate-800',
    render: (row) => displayCell(row.company_name),
  },
  {
    key: 'property_name',
    label: 'Property',
    sortable: true,
    className: 'min-w-[9rem] whitespace-normal text-slate-800',
    render: (row) => displayCell(row.property_name),
  },
  {
    key: 'property_address',
    label: 'Address',
    sortable: true,
    className: 'min-w-[10rem] whitespace-normal text-slate-700',
    render: (row) => displayCell(row.property_address),
  },
  {
    key: 'property_city',
    label: 'City',
    sortable: true,
    className: 'whitespace-nowrap text-slate-700',
    render: (row) => displayCell(row.property_city),
  },
  {
    key: 'property_state',
    label: 'State',
    sortable: true,
    className: 'whitespace-nowrap text-slate-700',
    render: (row) => displayCell(row.property_state),
  },
  {
    key: 'property_zipcode',
    label: 'Zipcode',
    sortable: true,
    className: 'whitespace-nowrap font-mono text-xs tabular-nums text-slate-700',
    render: (row) => displayCell(row.property_zipcode),
  },
  {
    key: 'unit_number',
    label: 'Unit',
    sortable: true,
    className: 'whitespace-nowrap text-slate-700',
    render: (row) => displayCell(row.unit_number),
  },
  {
    key: 'contract_start_date',
    label: 'Contract Start',
    sortable: true,
    className: DATE_CELL,
    render: (row) => formatDateCell(row.contract_start_date),
  },
  {
    key: 'contract_end_date',
    label: 'Contract End',
    sortable: true,
    className: DATE_CELL,
    render: (row) => formatDateCell(row.contract_end_date),
  },
  {
    key: 'moveout_date',
    label: 'Move Out Date',
    sortable: true,
    className: DATE_CELL,
    render: (row) => formatDateCell(row.moveout_date),
  },
  {
    key: 'Week',
    label: 'Week',
    sortable: true,
    className: 'whitespace-nowrap tabular-nums text-slate-700',
    render: (row) => (isWeekSummaryRow(row) ? '' : displayCell(row.Week)),
  },
  {
    key: 'rent_amount',
    label: 'Rent Amount',
    sortable: true,
    align: 'right',
    headerClassName: MONEY_HEADER,
    className: MONEY_CELL,
    render: (row) => moneyCell(row.rent_amount),
  },
  {
    key: 'balance_due',
    label: 'Balance Due',
    sortable: true,
    align: 'right',
    headerClassName: MONEY_HEADER,
    className: MONEY_CELL,
    render: (row) => moneyCell(row.balance_due),
  },
  {
    key: 'paid',
    label: 'Paid',
    align: 'right',
    headerClassName: MONEY_HEADER,
    className: MONEY_CELL,
    render: (row) => moneyCell(row.paid),
  },
  {
    key: 'refunded',
    label: 'Refunded',
    align: 'right',
    headerClassName: MONEY_HEADER,
    className: MONEY_CELL,
    render: (row) => moneyCell(row.refunded),
  },
  {
    key: 'mgr_premium',
    label: 'MRG Premium',
    align: 'right',
    headerClassName: MONEY_HEADER,
    className: MONEY_CELL,
    render: (row) => moneyCell(row.mgr_premium),
  },
  {
    key: 'mrg_percentage',
    label: 'MRG %',
    align: 'right',
    headerClassName: MONEY_HEADER,
    className: PCT_CELL,
    render: (row) =>
      row.mrg_percentage == null || row.mrg_percentage === ''
        ? ''
        : `${row.mrg_percentage}%`,
  },
  {
    key: 'application_status',
    label: 'Application Status',
    sortable: true,
    className: 'whitespace-nowrap text-slate-700',
    render: (row) => displayCell(row.application_status),
  },
  {
    key: 'rental_status',
    label: 'Rental Status',
    sortable: true,
    className: 'whitespace-nowrap text-slate-700',
    render: (row) => displayCell(row.rental_status),
  },
  {
    key: 'rental_progress',
    label: 'Progress',
    sortable: true,
    className: 'whitespace-nowrap text-slate-700',
    render: (row) => displayCell(row.rental_progress),
  },
  {
    key: 'reimbursement_type_label',
    label: 'Reimbursement Type',
    sortable: true,
    className: 'whitespace-nowrap text-slate-700',
    render: (row) => displayCell(row.reimbursement_type_label),
  },
  {
    key: 'claim_status',
    label: 'Reimbursement Status',
    sortable: true,
    className: 'whitespace-nowrap text-slate-700',
    render: (row) => displayCell(row.claim_status),
  },
  {
    key: 'rental_status_date',
    label: 'Reimbursement Notice Date',
    sortable: true,
    className: DATE_CELL,
    render: (row) => formatDateCell(row.rental_status_date),
  },
  {
    key: 'first_month_not_paid',
    label: 'First Month Not Paid',
    sortable: true,
    className: DATE_CELL,
    render: (row) => formatDateCell(row.first_month_not_paid),
  },
  {
    key: 'net_claim_payment',
    label: 'Net Reimbursements Payment',
    align: 'right',
    headerClassName: MONEY_HEADER,
    className: MONEY_CELL,
    render: (row) => moneyCell(row.net_claim_payment),
  },
  {
    key: 'default_paid',
    label: 'Paid Amount',
    sortable: true,
    align: 'right',
    headerClassName: MONEY_HEADER,
    className: MONEY_CELL,
    render: (row) => moneyCell(row.default_paid),
  },
  {
    key: 'default_paid_date',
    label: 'Paid Date',
    sortable: true,
    className: DATE_CELL,
    render: (row) => formatDateCell(row.default_paid_date),
  },
  {
    key: 'recovery_amount',
    label: 'Recovery Amount',
    sortable: true,
    align: 'right',
    headerClassName: MONEY_HEADER,
    className: MONEY_CELL,
    render: (row) => moneyCell(row.recovery_amount),
  },
  {
    key: 'recovery_date',
    label: 'Recovery Date',
    sortable: true,
    className: DATE_CELL,
    render: (row) => formatDateCell(row.recovery_date),
  },
]
